<template>
  <div class="space-y-1.5 w-full">
    <!-- Label -->
    <label
      v-if="label"
      class="block text-xs font-semibold uppercase tracking-wider text-slate-600"
    >
      {{ label }}
      <span
        v-if="required"
        class="text-rose-500"
        >*</span
      >
    </label>

    <!-- Dropzone / Preview Area -->
    <div
      class="group relative overflow-hidden rounded-xl border-2 border-dashed transition-all duration-200"
      :class="[
        isDragging
          ? 'border-indigo-500 bg-indigo-50/60 ring-4 ring-indigo-500/10'
          : error
          ? 'border-rose-300 bg-rose-50/30'
          : previewUrl
          ? 'border-slate-200/80 bg-slate-900/5 hover:border-indigo-300'
          : 'border-slate-200 bg-slate-50/50 hover:border-slate-300 hover:bg-slate-100/50',
      ]"
      @dragover.prevent="isDragging = true"
      @dragleave.prevent="isDragging = false"
      @drop.prevent="handleDrop"
    >
      <!-- Hidden File Input -->
      <input
        ref="fileInput"
        type="file"
        accept="image/*"
        class="hidden"
        @change="handleFileSelect"
      />

      <!-- Existing / Selected Image Preview -->
      <div
        v-if="previewUrl"
        class="relative aspect-video w-full min-h-[160px] sm:min-h-[180px] overflow-hidden bg-slate-900"
      >
        <img
          :src="previewUrl"
          :alt="label || 'Uploaded preview'"
          class="h-full w-full object-cover transition-transform duration-300 group-hover:scale-105"
        />

        <!-- Dark Overlay -->
        <div
          class="absolute inset-0 bg-slate-950/30 opacity-0 transition-opacity duration-200 group-hover:opacity-100 pointer-events-none"
        ></div>

        <!-- Floating Action Controls -->
        <div
          class="absolute right-2.5 top-2.5 sm:right-3 sm:top-3 flex items-center space-x-2 z-10"
        >
          <button
            type="button"
            title="Replace Image"
            class="flex h-8 w-8 sm:h-9 sm:w-9 items-center justify-center rounded-lg bg-white/90 text-slate-700 shadow-md backdrop-blur-md transition hover:bg-white hover:text-indigo-600 active:scale-90 cursor-pointer"
            @click="triggerFileInput"
          >
            <RefreshCwIcon class="h-4 w-4" />
          </button>
          <button
            type="button"
            title="Remove Image"
            class="flex h-8 w-8 sm:h-9 sm:w-9 items-center justify-center rounded-lg bg-white/90 text-rose-600 shadow-md backdrop-blur-md transition hover:bg-rose-600 hover:text-white active:scale-90 cursor-pointer"
            @click="removeImage"
          >
            <Trash2Icon class="h-4 w-4" />
          </button>
        </div>

        <!-- File Status Tag -->
        <div
          class="absolute bottom-2.5 left-2.5 sm:bottom-3 sm:left-3 rounded-lg bg-slate-900/85 px-2.5 py-1 text-[10px] sm:text-[11px] font-semibold text-white backdrop-blur-md shadow-xs z-10"
        >
          <span
            v-if="isNewFile"
            class="text-emerald-400 flex items-center gap-1"
          >
            <span class="h-1.5 w-1.5 rounded-full bg-emerald-400"></span>
            Ready to Upload (New)
          </span>
          <span
            v-else
            class="text-slate-300"
          >
            Cloudinary Media
          </span>
        </div>
      </div>

      <!-- Blank Dropzone UI -->
      <div
        v-else
        class="flex min-h-[150px] sm:min-h-[170px] cursor-pointer flex-col items-center justify-center p-4 sm:p-6 text-center transition"
        @click="triggerFileInput"
      >
        <div
          class="mb-2.5 flex h-10 w-10 sm:h-12 sm:w-12 items-center justify-center rounded-xl bg-indigo-50 text-indigo-600 transition-transform group-hover:scale-110 shadow-xs"
        >
          <UploadCloudIcon class="h-5 w-5 sm:h-6 sm:w-6" />
        </div>
        <p class="text-xs font-bold text-slate-700">
          Click to upload
          <span class="font-normal text-slate-500">or drag and drop</span>
        </p>
        <p class="mt-1 text-[10px] sm:text-[11px] text-slate-400">
          PNG, JPG, WEBP, GIF up to 5MB
        </p>
      </div>
    </div>

    <!-- Error Message -->
    <p
      v-if="error"
      class="flex items-center gap-1 text-xs font-medium text-rose-500 pt-0.5"
    >
      <AlertCircleIcon class="h-3.5 w-3.5 flex-shrink-0" />
      <span>{{ error }}</span>
    </p>
  </div>
</template>

<script setup>
  import { ref, watch, onUnmounted } from "vue";
  import {
    UploadCloud as UploadCloudIcon,
    RefreshCw as RefreshCwIcon,
    Trash2 as Trash2Icon,
    AlertCircle as AlertCircleIcon,
  } from "lucide-vue-next";

  const props = defineProps({
    modelValue: {
      type: [String, File, Object],
      default: "",
    },
    label: {
      type: String,
      default: "",
    },
    required: {
      type: Boolean,
      default: false,
    },
    error: {
      type: String,
      default: "",
    },
  });

  const emit = defineEmits(["update:modelValue", "change"]);

  const fileInput = ref(null);
  const isDragging = ref(false);
  const previewUrl = ref("");
  const isNewFile = ref(false);
  let createdObjectUrl = null;

  const cleanupObjectUrl = () => {
    if (createdObjectUrl) {
      URL.revokeObjectURL(createdObjectUrl);
      createdObjectUrl = null;
    }
  };

  watch(
    () => props.modelValue,
    (newVal) => {
      cleanupObjectUrl();

      if (!newVal) {
        previewUrl.value = "";
        isNewFile.value = false;
      } else if (typeof newVal === "string") {
        previewUrl.value = newVal;
        isNewFile.value = false;
      } else if (newVal instanceof File) {
        createdObjectUrl = URL.createObjectURL(newVal);
        previewUrl.value = createdObjectUrl;
        isNewFile.value = true;
      } else if (typeof newVal === "object") {
        previewUrl.value = newVal.url || newVal.secure_url || newVal.src || "";
        isNewFile.value = false;
      }
    },
    { immediate: true },
  );

  const triggerFileInput = () => {
    fileInput.value?.click();
  };

  const handleFileSelect = (event) => {
    const file = event.target.files?.[0];
    if (file) processFile(file);
  };

  const handleDrop = (event) => {
    isDragging.value = false;
    const file = event.dataTransfer?.files?.[0];
    if (file && file.type.startsWith("image/")) {
      processFile(file);
    }
  };

  const processFile = (file) => {
    cleanupObjectUrl();
    isNewFile.value = true;
    createdObjectUrl = URL.createObjectURL(file);
    previewUrl.value = createdObjectUrl;
    emit("update:modelValue", file);
    emit("change", file);
  };

  const removeImage = () => {
    cleanupObjectUrl();
    previewUrl.value = "";
    isNewFile.value = false;
    if (fileInput.value) fileInput.value.value = "";
    emit("update:modelValue", "");
    emit("change", "");
  };

  onUnmounted(() => {
    cleanupObjectUrl();
  });
</script>
